import React from "react";
import { InputNumber } from "primereact/inputnumber";
import localeText from "../../../../../../../locale";
import FormSectionHeader from "../components/FormSectionHeader";
import ShippingOptionCards from "../components/ShippingOptionCards";

function PricingSection({ form }) {
  const isService = form.productType === 2;
  const isPhysical = form.productType === 1;
  const showPrice = !form.hasVariations;
  const showShipping = isPhysical && form.hasToCalculateFreight;
  const titleKey = isPhysical && form.hasVariations ? "discountAndShippingDetails" : "priceDetails";
  const subtitleKey = isService
    ? "priceDetailsSubtitleService"
    : isPhysical && form.hasVariations
      ? "discountAndShippingSubtitle"
      : "priceDetailsSubtitle";
  const svgIcon = (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.96008 0L20.354 1.48575L21.8387 11.8808L12.1871 21.5323C11.9902 21.7292 11.7232 21.8398 11.4448 21.8398C11.1664 21.8398 10.8993 21.7292 10.7024 21.5323L0.307426 11.1373C0.110581 10.9404 0 10.6734 0 10.395C0 10.1166 0.110581 9.84955 0.307426 9.65265L9.96008 0ZM12.9295 8.9103C13.1245 9.10527 13.3561 9.25992 13.6109 9.36541C13.8657 9.47091 14.1388 9.52518 14.4145 9.52513C14.6903 9.52508 14.9634 9.47071 15.2182 9.36513C15.4729 9.25955 15.7044 9.10482 15.8994 8.90977C16.0944 8.71473 16.249 8.4832 16.3545 8.22839C16.46 7.97358 16.5143 7.70049 16.5142 7.4247C16.5142 7.14892 16.4598 6.87585 16.3542 6.62108C16.2486 6.3663 16.0939 6.13482 15.8989 5.93985C15.7038 5.74488 15.4723 5.59023 15.2175 5.48474C14.9627 5.37924 14.6896 5.32497 14.4138 5.32502C13.8568 5.32512 13.3227 5.54647 12.929 5.94038C12.5352 6.33428 12.314 6.86848 12.3141 7.42545C12.3142 7.98242 12.5356 8.51653 12.9295 8.9103Z"
        fill="var(--theme-secondary)"
      />
    </svg>
  );
  return (
    <section className="product-form-card flex flex-column gap-4">
      <FormSectionHeader
        icon={svgIcon}
        title={localeText(titleKey)}
        subtitle={localeText(subtitleKey)}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full m-auto">
        {showPrice && (
          <div className="flex-1">
            <label className="product-form-label">
              {localeText("price")}
              <span className="product-form-required">*</span>
            </label>
            <InputNumber
              value={form.priceUnique}
              onValueChange={(e) => form.setPriceUnique(e.value)}
              mode="currency"
              currency={form.currencyCode || "EUR"}
              locale="de-DE"
              className="w-full"
              placeholder="0,00"
            />
            <p className="product-form-help">
              {localeText(isService ? "priceHelpService" : "priceHelp")}
            </p>
          </div>
        )}
        <div className="flex-1">
          <label className="product-form-label">{localeText("discountOptional")}</label>
          <InputNumber
            value={form.discountPercent}
            onValueChange={(e) => form.setDiscountPercent(e.value)}
            suffix="%"
            className="w-full"
            min={0}
            max={99}
          />
          <p className="product-form-help">
            {localeText(isService ? "discountHelpService" : "discountHelp")}
          </p>
        </div>
        <div className="flex-1">
          <label className="product-form-label">
            {localeText("cashbackPercent")}
            <span className="product-form-required">*</span>
          </label>
          <InputNumber
            value={form.percentExtraCoin}
            onValueChange={(e) => form.setPercentExtraCoin(e.value)}
            suffix="%"
            className="w-full"
            min={form.cashbackBounds.min}
            max={form.cashbackBounds.max}
          />
          <p className="product-form-help">
            {localeText("cashbackHelpPrefix")}{" "}
            <button type="button" className="product-form-link underline" onClick={form.openCostCalculator}>
              {localeText("priceCalculator")}
            </button>
            .
          </p>
        </div>
      </div>

      {showShipping && (
        <>
          <div className="flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <div className="text-xl font-medium">{localeText("productDimensions")}</div>
              <div
                className="text-sm"
                style={{ color: "var(--theme-text-muted)", marginTop: "0.25rem" }}
              >
                {localeText("productDimensionsSubtitle")}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 w-full m-auto">
            {[
              ["heightCm", form.height, form.setHeight],
              ["widthCm", form.width, form.setWidth],
              ["lengthCm", form.length, form.setLength],
              ["weightKg", form.weight, form.setWeight],
            ].map(([key, value, setter]) => (
              <div key={key} className="flex-1">
                <label className="product-form-label">
                  {localeText(key)}
                  <span className="product-form-required">*</span>
                </label>
                <InputNumber
                  value={value}
                  onValueChange={(e) => setter(e.value)}
                  className="w-full"
                  min={0}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={3}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-column gap-2">
            <div className="text-xl font-medium">{localeText("shipping")}</div>
            <div className="flex justify-content-between align-items-center">
              <div className="product-form-help m-0">
                {localeText("shippingHelp")}
              </div>
              <button
                type="button"
                className="product-form-link text-xs font-medium flex align-items-center gap-1"
                onClick={form.openCostCalculator}
              >
                <i className="pi pi-calculator text-sm" />{" "}
                <span className="underline">{localeText("shippingSimulator")}</span>
              </button>
            </div>
            <ShippingOptionCards
              value={form.freightOption}
              onChange={form.setFreightOption}
              showSendCloud={!form.notHaveFreeSendCloud}
            />
          </div>
        </>
      )}
    </section>
  );
}

export default PricingSection;
